import {
  useFloating,
  offset as offsetMiddleware,
  flip,
  shift,
  arrow as arrowMiddleware,
  autoUpdate,
  useDismiss,
  useInteractions,
  FloatingPortal,
  Placement,
} from '@floating-ui/react';
import * as React from 'react';

import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { colors } from 'styles/cp';
import { popoverStyles } from 'styles/popover';

const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/Shared/Popover/CpPopover';

export interface CpPopoverProps {
  target: string;
  isOpen?: boolean;
  placement?: Placement;
  children: React.ReactNode;
  className?: string;
  innerClassName?: string;
  showArrow?: boolean;
  toggle?: () => void;
  trigger?: 'click' | 'hover';
  offset?: string;
  modifiers?: {
    offset?: { enabled: boolean; offset: string };
  };
  autoHide?: boolean;
}

function parseOffsetString(raw: string): { mainAxis: number; crossAxis: number } {
  const parts = raw
    .replace(/px/g, '')
    .split(',')
    .map((s) => Number(s.trim()));
  return {
    crossAxis: parts[0] || 0,
    mainAxis: parts[1] || 0,
  };
}

function resolveOffset(
  props: Pick<CpPopoverProps, 'offset' | 'modifiers'>
): { mainAxis: number; crossAxis: number } | undefined {
  if (props.modifiers?.offset?.enabled && props.modifiers.offset.offset) {
    return parseOffsetString(props.modifiers.offset.offset);
  }
  if (props.offset) {
    return parseOffsetString(props.offset);
  }
  return undefined;
}

function getDirectionClass(placement: string): string {
  const base = placement.split('-')[0];
  return `bs-popover-${base}`;
}

const CpPopover: React.FC<CpPopoverProps> = (props) => {
  const {
    target,
    isOpen,
    placement = 'bottom',
    children,
    className,
    innerClassName,
    showArrow,
    toggle,
    trigger,
  } = props;

  const arrowRef = React.useRef<HTMLDivElement>(null);

  const getTargetElement = React.useCallback((): Element | null => {
    try {
      if (typeof window === 'undefined') return null;
      return document.getElementById(target);
    } catch (err) {
      splunkReporter.contextual({
        event: 'popover' as any,
        action: 'getElementByIdError' as any,
        logInfo: { logLevel: 'error', logger },
        activityInfo: {
          message: 'Failed to get element by ID - querySelectorAll crash prevented',
          target,
          errorType: 'getElementById_failed',
        },
        error: {
          message: (err as Error)?.message || 'Unknown error',
          stack: (err as Error)?.stack || '',
        },
      });
      return null;
    }
  }, [target]);

  const [targetElement, setTargetElement] = React.useState<Element | null>(getTargetElement);

  React.useEffect(() => {
    setTargetElement(getTargetElement());
  }, [target, getTargetElement]);

  React.useEffect(() => {
    if (!targetElement || !toggle) return;

    if (trigger === 'hover') {
      const onEnter = () => {
        if (!isOpen) toggle();
      };
      const onLeave = () => {
        if (isOpen) toggle();
      };
      targetElement.addEventListener('mouseenter', onEnter);
      targetElement.addEventListener('mouseleave', onLeave);
      return () => {
        targetElement.removeEventListener('mouseenter', onEnter);
        targetElement.removeEventListener('mouseleave', onLeave);
      };
    }

    if (trigger === 'click') {
      const onClick = () => toggle();
      targetElement.addEventListener('click', onClick);
      return () => {
        targetElement.removeEventListener('click', onClick);
      };
    }

    return undefined;
  }, [targetElement, trigger, toggle, isOpen]);

  const offsetConfig = resolveOffset(props);
  const middleware = [
    offsetConfig ? offsetMiddleware(offsetConfig) : offsetMiddleware(0),
    flip(),
    shift(),
    ...(showArrow ? [arrowMiddleware({ element: arrowRef })] : []),
  ];

  const {
    refs,
    floatingStyles,
    context,
    placement: resolvedPlacement,
  } = useFloating({
    placement,
    middleware,
    open: isOpen,
    onOpenChange: (open: boolean) => {
      if (!open && toggle) {
        toggle();
      }
    },
    elements: {
      reference: targetElement,
    },
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, { enabled: Boolean(toggle) });
  const { getFloatingProps } = useInteractions([dismiss]);

  if (!targetElement || !isOpen) {
    return <React.Fragment />;
  }

  const directionClass = getDirectionClass(resolvedPlacement);
  const outerClasses = ['popover', directionClass, className].filter(Boolean).join(' ');

  return (
    <React.Fragment>
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={outerClasses}
          {...getFloatingProps()}
        >
          {showArrow && <div className="arrow" ref={arrowRef} />}
          <div className={innerClassName}>{children}</div>
        </div>
      </FloatingPortal>

      <style jsx global>{`
        .popover {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 1002;
          display: block;
          word-wrap: break-word;
          background-color: ${colors.white};
          background-clip: padding-box;
          border-radius: 4px;
          box-shadow: 0 3px 3px 0 ${popoverStyles.menuItemDividerBorderColor};

          .arrow {
            position: absolute;
            display: block;
            width: ${popoverStyles.popoverArrowWidth};
            height: ${popoverStyles.popoverArrowHeight};
            margin: 0 ${popoverStyles.borderRadiusLg};

            &::before,
            &::after {
              position: absolute;
              display: block;
              content: '';
              border-color: ${showArrow
                ? `${colors.gray} transparent transparent transparent`
                : 'transparent'};
              border-style: solid;
              left: ${showArrow ? '50%' : 'auto'};
              margin-left: ${showArrow ? '-10px' : '0'};
              border-width: ${showArrow ? '10px' : '0'};
            }
          }
        }

        .bs-popover-top {
          margin-bottom: ${popoverStyles.popoverArrowHeight};

          .arrow {
            bottom: calc(
              calc(${popoverStyles.popoverArrowHeight} + ${popoverStyles.popoverBorderWidth}) * -1
            );
          }

          .arrow::before,
          .arrow::after {
            border-width: ${popoverStyles.popoverArrowHeight}
              calc(${popoverStyles.popoverArrowWidth} / 2) 0;
          }

          .arrow::before {
            bottom: 1px;
            border-top-color: ${popoverStyles.popoverArrowOuterColor};
          }

          .arrow::after {
            bottom: ${popoverStyles.popoverBorderWidth};
          }
        }

        .bs-popover-right {
          margin-left: ${popoverStyles.popoverArrowHeight};

          .arrow {
            left: calc(
              calc(${popoverStyles.popoverArrowHeight} + ${popoverStyles.popoverBorderWidth}) * -1
            );
            width: ${popoverStyles.popoverArrowHeight};
            height: ${popoverStyles.popoverArrowWidth};
            margin: ${popoverStyles.borderRadiusLg} 0;
          }

          .arrow::before,
          .arrow::after {
            border-width: calc(${popoverStyles.popoverArrowWidth} / 2)
              ${popoverStyles.popoverArrowHeight} calc(${popoverStyles.popoverArrowWidth} / 2) 1px;
          }

          .arrow::before {
            left: 0;
            border-right-color: ${popoverStyles.popoverArrowOuterColor};
          }

          .arrow::after {
            left: ${popoverStyles.popoverBorderWidth};
            border-right-color: ${popoverStyles.popoverArrowColor};
          }
        }

        .bs-popover-bottom {
          margin-top: ${popoverStyles.popoverArrowHeight};

          .arrow {
            top: calc(
              (${popoverStyles.popoverArrowHeight} + ${popoverStyles.popoverBorderWidth}) * -1
            );
          }

          .arrow::before,
          .arrow::after {
            border-width: 0 calc(${popoverStyles.popoverArrowWidth} / 2)
              ${popoverStyles.popoverArrowHeight} calc(${popoverStyles.popoverArrowWidth} / 2);
          }

          .arrow::before {
            top: 0;
          }

          .arrow::after {
            top: ${popoverStyles.popoverBorderWidth};
            border-bottom-color: ${popoverStyles.popoverArrowColor};
          }

          .popover-header::before {
            position: absolute;
            top: 0;
            left: 50%;
            display: block;
            width: ${popoverStyles.popoverArrowWidth};
            margin-left: calc(${popoverStyles.popoverArrowWidth} / -2);
            content: '';
            border-bottom: ${popoverStyles.popoverBorderWidth} solid
              ${popoverStyles.popoverHeaderBg};
          }
        }

        .bs-popover-left {
          margin-right: ${popoverStyles.popoverArrowHeight};

          .arrow {
            right: calc(
              (${popoverStyles.popoverArrowHeight} + ${popoverStyles.popoverBorderWidth}) * -1
            );
            width: ${popoverStyles.popoverArrowHeight};
            height: ${popoverStyles.popoverArrowWidth};
            margin: ${popoverStyles.borderRadiusLg} 0;
          }

          .arrow::before,
          .arrow::after {
            border-width: calc(${popoverStyles.popoverArrowWidth} / 2) 1px
              calc(${popoverStyles.popoverArrowWidth} / 2) ${popoverStyles.popoverArrowHeight};
          }

          .arrow::before {
            right: 0;
            border-left-color: ${popoverStyles.popoverArrowOuterColor};
          }

          .arrow::after {
            right: ${popoverStyles.popoverBorderWidth};
            border-left-color: ${popoverStyles.popoverArrowColor};
          }
        }

        .cp-menu-wrapper {
          border: ${popoverStyles.popoverBorderWidth} solid
            ${popoverStyles.popoverUserMenuBorderColor};

          .arrow {
            text-shadow: 0 0 20px black;
          }

          &.bs-popover-top {
            .arrow::before {
              bottom: 0;
              border-top-color: ${popoverStyles.whitePopoverArrowOuterColor};
            }

            .arrow::after {
              bottom: ${popoverStyles.popoverBorderWidth};
              border-top-color: ${popoverStyles.whitePopoverArrowColor};
            }
          }

          &.bs-popover-right {
            .arrow::before {
              left: 0;
              border-right-color: ${popoverStyles.whitePopoverArrowOuterColor};
            }

            .arrow::after {
              left: ${popoverStyles.popoverBorderWidth};
              border-right-color: ${popoverStyles.whitePopoverArrowColor};
            }
          }

          &.bs-popover-bottom {
            .arrow::before {
              top: 0;
              border-bottom-color: ${popoverStyles.whitePopoverArrowOuterColor};
            }

            .arrow::after {
              top: ${popoverStyles.popoverBorderWidth};
              border-bottom-color: ${popoverStyles.whitePopoverArrowColor};
            }

            .popover-header::before {
              position: absolute;
              top: 0;
              left: 50%;
              display: block;
              width: ${popoverStyles.popoverArrowWidth};
              margin-left: calc(${popoverStyles.popoverArrowWidth} / -2);
              content: '';
              border-bottom: ${popoverStyles.popoverBorderWidth} solid
                ${popoverStyles.popoverHeaderBg};
            }
          }

          &.bs-popover-left {
            .arrow::before {
              right: 0;
              border-left-color: ${popoverStyles.whitePopoverArrowOuterColor};
            }

            .arrow::after {
              right: ${popoverStyles.popoverBorderWidth};
              border-left-color: ${popoverStyles.whitePopoverArrowColor};
            }
          }
        }
      `}</style>
    </React.Fragment>
  );
};

export default CpPopover;
